import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { EkycStatus } from '../../core/enum/ekyc-status.enum';
import { Role } from '../../core/enum/role.enum';
import { AccountService } from '../../core/services/api/account.service';
import { SettingService } from '../../core/services/api/setting.service';
import { Item } from '../../core/models/share/item';
import { PaymentMethod } from '../../core/models/payment/payment-method';
import { UserProfile } from '../../core/models/user/user-profile';
import { ErrorService } from '../../core/services/error.service';
import { StorageService } from '../../core/services/storage.service';
import { ToastService } from '../../core/services/toast.service';
import { PopupService } from '../../shared/services/popup/popup.service';
import { detectIdentityChange } from '../../shared/profile-section-forms/detect-identity-change';
import { parseRequiredSections } from './required-section';
import { SectionEditSheetComponent } from './section-edit-sheet/section-edit-sheet.component';
import {
  ReviewAttestSection,
  SectionStatus,
  canConfirmEverythingCorrect,
  computeSectionStatuses,
  isSectionEmpty,
} from './section-status.util';

// Entry-point framing driven by the optional `context` query param — a routine
// account-details check should read as reassuring, never as a lock/suspend.
// The copy is data (not hardcoded) so new entry points only add a map row.
// No lock/blocked/suspend language anywhere. Variant B (Statement) shape:
// an eyebrow, a statement title, a one-line assurance, and the primary CTA.
interface ContextCopy {
  eyebrow: string;
  title: string;
  assurance: string;
  cta: string;
}

const DEFAULT_CONTEXT_COPY: ContextCopy = {
  eyebrow: 'Account check',
  title: 'Review your details',
  assurance:
    'A quick once-over — about a minute. As a regulated platform we check in now and then to keep your records current. Nothing about your money or investments changes.',
  cta: 'Confirm my details',
};

const CONTEXT_COPY: Record<string, ContextCopy> = {
  withdraw: {
    eyebrow: 'Before your withdrawal',
    title: 'One quick check, then your withdrawal continues',
    assurance:
      'Your money is safe. We just need your details up to date before we send a withdrawal — it takes about a minute.',
    cta: 'Confirm and continue',
  },
  invest: {
    eyebrow: 'Before you invest',
    title: 'One quick check, then you can invest',
    assurance:
      'Your account is all set — we just need your details up to date before you invest. It takes about a minute.',
    cta: 'Confirm and continue',
  },
  topup: {
    eyebrow: 'Before your top-up',
    title: 'One quick check, then your top-up continues',
    assurance:
      'Your money is safe. We just need your details up to date before you add funds — it takes about a minute.',
    cta: 'Confirm and continue',
  },
};

// The profile review-and-attest screen: fetches live profile + payment-method
// data, computes section statuses, and edits each section IN PLACE via a
// bottom-sheet modal (INC-289 Phase 2) rather than navigating away.
// Reachable from the login nudge, the investing/eKYC gate, and the
// withdraw/top-up gates (see their re-pointed navigation call sites).
@Component({
  standalone: false,
  selector: 'app-profile-review-attest',
  templateUrl: './profile-review-attest.component.html',
  styleUrls: ['./profile-review-attest.component.scss'],
})
export class ProfileReviewAttestComponent implements OnInit {
  requiredSections: ReviewAttestSection[] = [];
  returnUrl: string | null = null;
  context: string | null = null;

  profile: UserProfile | null = null;
  paymentMethod: PaymentMethod | null = null;
  statuses: Record<ReviewAttestSection, SectionStatus> | null = null;
  role: Role;

  isLoading = false;
  isConfirming = false;

  // INC-322: set while the "discard staged identity change" call is in flight,
  // so the discard affordance shows its loading state and can't be double-fired.
  discarding = false;

  // INC-289: the explicit attestation the final confirm is gated on.
  attestationChecked = false;

  // INC-289: set when a name/IC change is staged this session (Verified profile
  // → held pending in ReKYC). The two-step design keeps the investor on this
  // screen after the edit — the identity card flips to 'reverify' and the final
  // Confirm is what hands off to re-KYC (/proceed-e-kyc), not the section save.
  // Fails-closed is unchanged: the pending identity only commits on a re-KYC pass.
  identityReKycPending = false;

  // In-place-edit UI state: which section is mid-save (disables its Change
  // button), which just saved (brief highlight), and any persistent per-section
  // save error (shown inline, not as a fading toast).
  savingSection: ReviewAttestSection | null = null;
  highlightedSection: ReviewAttestSection | null = null;
  sectionErrors: Record<ReviewAttestSection, string | null> = {
    identity: null,
    contact: null,
    employmentIncome: null,
    bank: null,
  };

  // Employment-sector dropdown, loaded to map the stored id → its display name
  // in the Employment section body.
  employmentSectors: Item[] = [];

  // Occupation / employment-status dropdown, loaded to map the stored id → its
  // display name (id 255 = "Other", whose free-text sits in othersOccupationType).
  occupationTypes: Item[] = [];

  // INC-317: income-range + transaction-purpose dropdowns, loaded to map the
  // stored numeric ids → their display names in the Income & transaction body.
  grossAnnualIncome: Item[] = [];
  transactionPurposes: Item[] = [];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly accountService: AccountService,
    private readonly settingService: SettingService,
    private readonly errorService: ErrorService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly popupService: PopupService,
    private readonly storageService: StorageService,
  ) {}

  ngOnInit(): void {
    const queryParamMap = this.activatedRoute.snapshot.queryParamMap;
    this.requiredSections = parseRequiredSections(queryParamMap.get('required'));
    this.returnUrl = queryParamMap.get('returnUrl');
    this.context = queryParamMap.get('context');
    this.role = this.storageService.role;

    this.loadDropdowns();
    this.loadData();
  }

  // Loads the label lookups the read-only section bodies render: the stored
  // numeric ids map to display names via these Item[] lists (one dropdown call
  // returns employmentSector, grossAnnualIncome and transactionPurpose).
  private loadDropdowns(): void {
    const observable =
      this.role == Role.IndividualInvestor
        ? this.settingService.getProfileDropdown()
        : this.settingService.getCorporateRepDropdown();

    observable.subscribe({
      next: (data) => {
        this.employmentSectors = data.employmentSector ?? [];
        this.occupationTypes = data.occupationType ?? [];
        this.grossAnnualIncome = data.grossAnnualIncome ?? [];
        this.transactionPurposes = data.transactionPurpose ?? [];
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  get employmentSectorName(): string | null {
    const id = this.profile?.employmentSector;
    if (id == null) {
      return null;
    }
    return this.employmentSectors.find((sector) => sector.id === id)?.name ?? null;
  }

  // Occupation / employment-status display name. When "Other" (255) is stored,
  // show the investor's free-text (othersOccupationType) instead of "Other".
  get occupationTypeName(): string | null {
    const id = this.profile?.occupationType;
    if (id == null) {
      return null;
    }
    if (id === 255) {
      return this.profile?.othersOccupationType ?? null;
    }
    return this.occupationTypes.find((item) => item.id === id)?.name ?? null;
  }

  get grossAnnualIncomeName(): string | null {
    const id = this.profile?.grossAnnualIncomeType;
    if (id == null) {
      return null;
    }
    return this.grossAnnualIncome.find((item) => item.id === id)?.name ?? null;
  }

  get transactionPurposeName(): string | null {
    const id = this.profile?.transactionPurpose;
    if (id == null) {
      return null;
    }
    return this.transactionPurposes.find((item) => item.id === id)?.name ?? null;
  }

  get contextCopy(): ContextCopy {
    return (this.context && CONTEXT_COPY[this.context]) || DEFAULT_CONTEXT_COPY;
  }

  get canConfirmEverythingCorrect(): boolean {
    return this.statuses ? canConfirmEverythingCorrect(this.statuses) : false;
  }

  // Final confirm is enabled only when no required section is missing AND the
  // investor has explicitly attested. Kept separate from the status-only gate
  // so the template can explain WHICH condition is unmet.
  get canSubmitAttestation(): boolean {
    return this.canConfirmEverythingCorrect && this.attestationChecked;
  }

  // Names the still-missing required sections so the disabled confirm is never
  // silent. These are the blocker lines shown in the attest box.
  get missingSectionMessages(): string[] {
    if (!this.statuses) {
      return [];
    }

    // Bank ask is context-aware — a withdrawal explains where the money goes.
    const bankMessage =
      this.context === 'withdraw'
        ? 'Add a bank account so we can send your withdrawal.'
        : 'Add your bank account details.';

    const labels: Record<ReviewAttestSection, string> = {
      identity: 'Add your identity details.',
      contact: 'Add your contact details.',
      employmentIncome:
        'Add your employment and income details — sector and purpose of transaction are required before you can confirm.',
      bank: bankMessage,
    };

    return (['identity', 'contact', 'employmentIncome', 'bank'] as ReviewAttestSection[])
      .filter((section) => this.statuses![section] === 'missing')
      .map((section) => labels[section]);
  }

  // The attention callout above the grid links to each missing section by its
  // anchor (#section-<key>). Human-readable section titles for the links.
  get missingSections(): { key: ReviewAttestSection; label: string }[] {
    if (!this.statuses) {
      return [];
    }

    const labels: Record<ReviewAttestSection, string> = {
      identity: 'Identity',
      contact: 'Contact',
      employmentIncome: 'Employment & income',
      bank: 'Bank',
    };

    return (['identity', 'contact', 'employmentIncome', 'bank'] as ReviewAttestSection[])
      .filter((section) => this.statuses![section] === 'missing')
      .map((section) => ({ key: section, label: labels[section] }));
  }

  // Callout heading, pluralised: "1 thing needs…" / "N things need…".
  get calloutHeading(): string {
    const count = this.missingSections.length;
    return count === 1
      ? '1 thing needs your attention'
      : `${count} things need your attention`;
  }

  // Final CTA label: per-context, unless a name/ID change is staged this
  // session — then the confirm is the hand-off to identity re-verification.
  get confirmCtaLabel(): string {
    if (this.statuses?.identity === 'reverify' || this.identityReKycPending) {
      return 'Confirm and verify my identity';
    }
    return this.contextCopy.cta;
  }

  // Friendly per-section placeholder shown when the section has no data yet.
  emptyHint(section: ReviewAttestSection): string {
    switch (section) {
      case 'identity':
        return 'No identity details yet — add them to get started.';
      case 'contact':
        return 'No contact details yet.';
      case 'employmentIncome':
        return 'No employment or income details yet — add them to continue.';
      case 'bank':
        return 'No bank account on file yet.';
    }
  }

  isSectionEmpty(section: ReviewAttestSection): boolean {
    return this.profile ? isSectionEmpty(section, this.profile, this.paymentMethod) : true;
  }

  // Masked to the last 4 digits for read-only display — this screen never
  // needs the full account number, only enough for the investor to recognise
  // which account is on file.
  get maskedAccountNumber(): string | null {
    const accountNumber = this.paymentMethod?.accountNumber;
    if (!accountNumber) {
      return null;
    }

    return accountNumber.length > 4 ? `•••• ${accountNumber.slice(-4)}` : accountNumber;
  }

  confirmEverythingCorrect(): void {
    // INC-289 two-step: when a name/IC change was staged this session the
    // profile sits in ReKYC — Confirm is the single hand-off to identity
    // re-verification (CTOS), not attest (attest can't clear a re-KYC anyway).
    // Skipping/abandoning re-KYC leaves the profile in its "needs verification"
    // state; the pending name/IC only commits on a pass (fails-closed).
    if (this.identityReKycPending) {
      this.router.navigate(['/proceed-e-kyc']);
      return;
    }

    this.isConfirming = true;

    this.accountService
      .attestIndividualInvestorProfile()
      .pipe(finalize(() => (this.isConfirming = false)))
      .subscribe({
        next: (response) => {
          if (response.cleared) {
            this.router.navigateByUrl(this.safeReturnUrl());
          } else if (response.reasons?.includes('EkycReverification')) {
            // INC-288 re-KYC mode: the flag demands identity re-verification
            // (server-side ProfileRefreshReason.EkycReverification — e.g. an
            // admin "require re-verification" flag, or EKYCStatus back to ReKYC)
            // even though no name/IC was changed on-screen this session. Confirm
            // is the single hand-off to CTOS, exactly as the staged-identity path
            // above; attest can't clear a re-KYC so we route instead of erroring.
            this.router.navigate(['/proceed-e-kyc']);
          } else {
            // A remaining SOFT/other reason (e.g. a missing mandatory field the
            // investor hasn't filled). Surface the server's actual reason codes
            // rather than a canned message, so an investor whose sections show OK
            // still learns why Confirm didn't clear the flag.
            const detail = response.reasons?.length ? ` (${response.reasons.join(', ')})` : '';
            this.toastService.error(`Some information still needs your attention.${detail}`);
          }
        },
        error: (err) => this.errorService.showError(err),
      });
  }

  // INC-322: drop a name/IC change staged this session (or already awaiting
  // re-verification on the backend) so the investor can edit their identity
  // again. Confirm first (mirrors the dirty-discard convention in
  // openSectionSheet), then call the backend discard endpoint; on success clear
  // the pending flag and restore the pre-staging Verified status so the pill
  // drops back to normal and the Identity card's "Change" button re-opens.
  discardPendingIdentityChange(): void {
    this.popupService
      .confirm(
        "We'll remove the change that's waiting for verification. Your current verified details stay exactly as they are, and you can edit again straight away.",
        {
          titleLabel: 'Undo your name/ID change?',
          okLabel: 'Undo change',
          cancelLabel: 'Keep it',
        },
      )
      .then(
        () => {
          this.discarding = true;
          this.accountService
            .discardPendingIdentityChange()
            .pipe(finalize(() => (this.discarding = false)))
            .subscribe({
              next: () => {
                this.identityReKycPending = false;
                this.sectionErrors.identity = null;
                // A staged change is only ever taken from a Verified investor
                // (detectIdentityChange gates on it) and the live fields were
                // never touched — so discarding returns the card to Verified.
                if (this.profile) {
                  this.profile = { ...this.profile, ekycStatus: EkycStatus.Verified };
                }
                this.recomputeStatuses();
                this.toastService.success(
                  'Change undone — you can edit your details again.',
                );
              },
              error: (err) => this.errorService.showError(err),
            });
        },
        () => {
          // "Keep it" / dismissed — leave the staged change in place.
        },
      );
  }

  // INC-289 Phase 2: edit a section in place via the bottom sheet instead of
  // navigating away. The sheet embeds the same section-form component the
  // standalone settings page uses (validation parity is automatic); the save
  // itself is owned here so the re-KYC route + in-place status re-derive stay
  // on this screen.
  openSectionSheet(section: ReviewAttestSection): void {
    let sheet: SectionEditSheetComponent;

    const modalRef = this.modalService.open(SectionEditSheetComponent, {
      windowClass: 'section-edit-sheet-modal',
      backdropClass: 'section-edit-sheet-backdrop',
      ariaLabelledBy: 'section-edit-sheet-title',
      scrollable: true,
      // Discard-confirm on a dirty cancel/Esc/backdrop. beforeDismiss runs for
      // programmatic dismiss too in this ng-bootstrap version, so the sheet's
      // Cancel button (modal.dismiss()) routes through here as well — one guard
      // for every dismiss path. Never disables Esc/backdrop (APG).
      beforeDismiss: () => {
        if (!sheet?.isDirty()) {
          return true;
        }

        return this.popupService
          .confirm('You have unsaved changes. Discard them?', {
            titleLabel: 'Discard changes?',
            okLabel: 'Discard',
            cancelLabel: 'Keep editing',
          })
          .then(
            () => true,
            () => false,
          );
      },
    } as NgbModalOptions);

    sheet = modalRef.componentInstance;
    sheet.section = section;
    sheet.profile = this.profile;
    sheet.paymentMethod = this.paymentMethod;
    sheet.role = this.role;

    modalRef.result.then(
      (payload) => this.onSheetSaved(section, payload),
      () => {
        // dismissed (cancel/discard) — nothing to persist.
      },
    );
  }

  private onSheetSaved(
    section: ReviewAttestSection,
    payload: Partial<UserProfile & PaymentMethod>,
  ): void {
    if (!payload) {
      return;
    }

    this.sectionErrors[section] = null;
    const previousStatus: SectionStatus = this.statuses ? this.statuses[section] : 'needsChange';

    // Optimistic badge flip while the save is in flight; reverted on failure,
    // re-derived from the fresh data on success.
    if (this.statuses) {
      this.statuses = { ...this.statuses, [section]: 'ok' };
    }
    this.savingSection = section;

    if (section === 'bank') {
      const merged = { ...this.paymentMethod, ...payload } as PaymentMethod;
      this.accountService
        .updateProfilePaymentMethods(merged)
        .pipe(finalize(() => (this.savingSection = null)))
        .subscribe({
          next: () => {
            this.paymentMethod = merged;
            this.recomputeStatuses();
            this.flashSaved(section);
          },
          error: (err) => this.revertSection(section, previousStatus, err),
        });
      return;
    }

    const original = this.profile as UserProfile;
    const merged = { ...this.profile, ...payload } as UserProfile;

    // INC-259/INC-289: a Verified investor changing name/IC/identity-type is
    // held pending by the backend (staged into ReKYC, live fields untouched —
    // fails-closed). Same shared helper the standalone profile page uses.
    const identityChanged =
      section === 'identity' && detectIdentityChange(original, merged, original.ekycStatus);

    this.accountService
      .updateProfile(merged, section)
      .pipe(finalize(() => (this.savingSection = null)))
      .subscribe({
        next: () => {
          if (identityChanged) {
            // INC-289 two-step: do NOT jump to re-KYC on save. Keep the investor
            // on the review screen — reflect the backend's ReKYC staging locally,
            // flip the identity card to 'reverify', and let the final Confirm be
            // the single hand-off point to /proceed-e-kyc.
            this.profile = { ...merged, ekycStatus: EkycStatus.ReKYC };
            this.identityReKycPending = true;
            this.recomputeStatuses();
            this.toastService.success("Saved. We'll verify your identity when you confirm.");
            return;
          }
          this.profile = merged;
          this.recomputeStatuses();
          this.flashSaved(section);
        },
        error: (err) => this.revertSection(section, previousStatus, err),
      });
  }

  private recomputeStatuses(): void {
    if (this.profile) {
      this.statuses = computeSectionStatuses(
        this.profile,
        this.paymentMethod,
        this.requiredSections,
      );
      // A staged identity change is data-complete (so computeSectionStatuses
      // scores it 'ok') but still owes a re-verification — overlay that so the
      // card and the final Confirm reflect the outstanding re-KYC.
      if (this.identityReKycPending) {
        this.statuses = { ...this.statuses, identity: 'reverify' };
      }
    }
  }

  private flashSaved(section: ReviewAttestSection): void {
    // Non-blocking confirmation + a brief highlight of the changed row.
    this.toastService.success('Your details have been updated.');
    this.highlightedSection = section;
    setTimeout(() => {
      if (this.highlightedSection === section) {
        this.highlightedSection = null;
      }
    }, 2500);
  }

  private revertSection(
    section: ReviewAttestSection,
    previousStatus: SectionStatus,
    err: any,
  ): void {
    if (this.statuses) {
      this.statuses = { ...this.statuses, [section]: previousStatus };
    }

    // INC-322 (AC#5): a name/IC save rejected because a change is ALREADY staged
    // (backend ERR_IDENTITY_CHANGE_IN_PROGRESS) is not a generic failure — the
    // investor can't save a second change until they drop the pending one.
    // Surface the server's real message and point them at the discard
    // affordance rather than dead-ending on "couldn't save". Reflect the staged
    // state so that affordance is guaranteed visible (e.g. after a reload where
    // the session flag was lost). Error shape reads defensively across the
    // API's casings — see investment.component.ts precedent.
    const errorCode =
      err?.Error?.Code || err?.Error?.code || err?.error?.Error?.Code || err?.error?.code || '';
    if (section === 'identity' && errorCode === 'ERR_IDENTITY_CHANGE_IN_PROGRESS') {
      this.identityReKycPending = true;
      this.recomputeStatuses();
      // The "Undo this change" action renders inline right below this error
      // (the identity section is now in its reverify/pending state), so the
      // message states the cause; the affordance is already on screen.
      this.sectionErrors[section] =
        "A name/ID change is already waiting to be verified, so we couldn't save a new one.";
      return;
    }

    // Surface the server's specific VALIDATION message (e.g. ERR_REQ_EMP_SECTOR
    // "Employment Sector is required.") rather than the generic catch-all, so the
    // investor learns exactly which field the save rejected. Reads the API's error
    // envelope defensively across its casings — same precedent as
    // investment.component.ts.
    //
    // Only trust the message for a genuine client/validation error (4xx). A 5xx /
    // the generic EXCEPTION envelope carries "Internal Server Error. Please contact
    // the administrator" — a populated, truthy body that is NOT useful to the
    // investor — so those fall back to the friendly line (INC-324 pr-review finding).
    const status =
      err?.Error?.StatusCode || err?.Error?.Status || err?.error?.Error?.StatusCode ||
      err?.error?.Error?.Status || err?.status || 0;
    const errorMessage =
      err?.Error?.Message || err?.Error?.message || err?.error?.Error?.Message || err?.error?.message || '';
    const isValidationError =
      errorCode !== 'EXCEPTION' && (status === 0 || (status >= 400 && status < 500));
    // Persistent inline error — the one message they must see stays put rather
    // than fading like a toast.
    this.sectionErrors[section] =
      (isValidationError && errorMessage) || "We couldn't save your changes. Please try again.";
  }

  // Guards `returnUrl` (attacker-influenceable via a bookmarkable query param)
  // before it reaches a navigation sink. INC-329: this is now the SOLE guard on
  // that value — `login.component.ts`'s `resolveNormalLandingUrl()` was removed
  // with the login-screen nudge, since the interrupt is raised after landing and
  // passes the investor's current in-app URL rather than a deferred destination.
  private safeReturnUrl(): string {
    const url = this.returnUrl;
    return url && url.startsWith('/') && !url.startsWith('//') ? url : '/dashboard';
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin([this.accountService.profile(true), this.accountService.getProfilePaymentMethods()])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ([profile, paymentMethod]) => {
          this.profile = profile;
          this.paymentMethod = paymentMethod;
          this.identityReKycPending = profile.ekycStatus === EkycStatus.ReKYC;
          this.recomputeStatuses();
        },
        (err) => this.errorService.showError(err),
      );
  }
}
